import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/** @type {Promise<typeof import('./c-size-recommendation.js')> | null} */
let sizeRecModulePromise = null;

function loadSizeRecModule() {
  if (!sizeRecModulePromise) {
    sizeRecModulePromise = import('./c-size-recommendation.js');
  }
  return sizeRecModulePromise;
}

/**
 * @returns {QuickAddDialog | null}
 */
function getQuickAddDialog() {
  const dialogComponent = document.getElementById('quick-add-dialog');
  return dialogComponent instanceof QuickAddDialog ? dialogComponent : null;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {number} */
  #openRequestId = 0;
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingFetches = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // Warm the product HTML cache before tap so open feels instant.
    this.addEventListener('pointerenter', this.#prefetchProductPage, { passive: true });
    this.addEventListener('focusin', this.#prefetchProductPage, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.removeEventListener('pointerenter', this.#prefetchProductPage);
    this.removeEventListener('focusin', this.#prefetchProductPage);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  #prefetchProductPage = () => {
    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#cachedContent.has(currentUrl) || this.#pendingFetches.has(currentUrl)) return;
    this.#loadProductGrid(currentUrl, { prefetch: true }).catch(() => {});
  };

  /**
   * Fetch + cache product grid HTML. Concurrent callers share one in-flight promise.
   * @param {string} productPageUrl
   * @param {{ prefetch?: boolean }} [options]
   * @returns {Promise<Element | null>}
   */
  async #loadProductGrid(productPageUrl, options = {}) {
    const cached = this.#cachedContent.get(productPageUrl);
    if (cached) return cached;

    const pending = this.#pendingFetches.get(productPageUrl);
    if (pending) return pending;

    const request = this.fetchProductPage(productPageUrl, { prefetch: options.prefetch })
      .then((html) => {
        if (!html) return null;
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (!gridElement) return null;
        const cloned = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(productPageUrl, cloned);
        return cloned;
      })
      .finally(() => {
        this.#pendingFetches.delete(productPageUrl);
      });

    this.#pendingFetches.set(productPageUrl, request);
    return request;
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    try {
      variantPicker.updateVariantPicker(newHtml);
    } catch {
      // Source picker missing in cached markup — keep the already-morphed picker.
    }
  }

  /**
   * Handles quick add button click — open immediately, fill as soon as HTML is ready.
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    const requestId = ++this.#openRequestId;
    const dialogComponent = getQuickAddDialog();
    if (!dialogComponent) return;

    this.#openQuickAddModal(dialogComponent, { loading: true });

    try {
      const productGrid = await this.#loadProductGrid(currentUrl);
      if (requestId !== this.#openRequestId) return;

      if (!productGrid) {
        this.#closeQuickAddModal();
        return;
      }

      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      if (requestId !== this.#openRequestId) return;

      this.#updateVariantPicker(productGrid);
      this.#setDialogLoading(dialogComponent, false);
      // Content morph while open can leave a thin bottom-sheet; nudge layout.
      this.#settleOpenDialog(dialogComponent);
    } catch (error) {
      if (requestId !== this.#openRequestId) return;
      console.error(error);
      this.#closeQuickAddModal();
    }
  };

  #resetScroll() {
    const dialogComponent = getQuickAddDialog();
    if (!dialogComponent) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    if (this.hasAttribute('stay-visible')) return;

    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /**
   * @param {QuickAddDialog} dialogComponent
   * @param {boolean} loading
   */
  #setDialogLoading(dialogComponent, loading) {
    const dialog = dialogComponent.refs?.dialog;
    dialog?.classList.toggle('is-loading', loading);
  }

  /**
   * @param {QuickAddDialog} dialogComponent
   * @param {{ loading?: boolean }} [options]
   */
  #openQuickAddModal = (dialogComponent, options = {}) => {
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    const dialog = dialogComponent.refs?.dialog;
    const loading = Boolean(options.loading);

    if (dialog) {
      // Clear leftover close animation state that leaves the sheet stuck mid-transition.
      dialog.classList.remove('dialog-closing');
      dialog.style.animation = '';
      this.#setDialogLoading(dialogComponent, loading);
    }

    if (loading) {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) modalContent.replaceChildren();
    }

    dialogComponent.showDialog();

    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /** @param {QuickAddDialog} dialogComponent */
  #settleOpenDialog(dialogComponent) {
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog?.open) {
      this.#openQuickAddModal(dialogComponent, { loading: false });
      return;
    }

    // Force a layout pass after morph so height:fit-content recalculates.
    dialog.style.maxHeight = 'none';
    void dialog.offsetHeight;
    dialog.style.maxHeight = '';
    this.#resetScroll();
  }

  #closeQuickAddModal = () => {
    this.#openRequestId += 1;
    const dialogComponent = getQuickAddDialog();
    if (!dialogComponent) return;

    this.#setDialogLoading(dialogComponent, false);
    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @param {{ prefetch?: boolean }} [options]
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl, options = {}) {
    if (!productPageUrl) return null;

    const isPrefetch = Boolean(options.prefetch);
    /** @type {AbortSignal | undefined} */
    let signal;

    if (isPrefetch) {
      // Prefetch is fire-and-forget; don't abort in-flight warms (click reuses them).
      signal = undefined;
    } else {
      this.#abortController?.abort();
      this.#abortController = new AbortController();
      signal = this.#abortController.signal;
    }

    try {
      const response = await fetch(productPageUrl, signal ? { signal } : undefined);

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (!isPrefetch) {
        this.#abortController = null;
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint() && !productGrid.querySelector('.c-pdp__info')) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      // Fall back to the custom price block (.c-pdp__price-row) when the stock
      // <product-price> element isn't present, otherwise mobile drops the price
      // (the whole .product-details is removed below).
      const productPrice = productGrid.querySelector('product-price, .c-pdp__price-row');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    loadSizeRecModule()
      .then(({ initQuickAddSizeRecommendation }) => initQuickAddSizeRecommendation(modalContent))
      .catch(() => {});
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    // The selected size radio carries the authoritative data-variant-id. The
    // variant-picker's own section fetch can resolve the wrong (default) variant
    // in the modal context, so mirror the checked radio straight into the form's
    // hidden id on every change, and re-assert after variantUpdate (deferred so
    // it runs after product-form-component overwrites the id) — otherwise the
    // wrong size gets added to cart.
    this.addEventListener('change', this.#syncSelectedVariantId, { signal: this.#abortController.signal });
    this.addEventListener(
      ThemeEvents.variantUpdate,
      () => queueMicrotask(this.#syncSelectedVariantId),
      { signal: this.#abortController.signal }
    );

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  #syncSelectedVariantId = () => {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('variant-picker input[type="radio"][data-variant-id]:checked')
    );
    const variantId = checked?.dataset.variantId;
    if (!variantId) return;

    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('product-form-component input[name="id"]')
    );
    if (variantIdInput && variantIdInput.value !== variantId) {
      variantIdInput.value = variantId;
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    loadSizeRecModule()
      .then(({ cleanupPortaledSizeRecModals }) => cleanupPortaledSizeRecModals())
      .catch(() => {});

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
